import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Plus, Star, Minus } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { formatRands } from "@/lib/order";
import { useCart } from "@/context/cart-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/common/States";
import { cn } from "@/lib/utils";

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [tab, setTab] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["vendor", id],
    queryFn: () => api(`/vendors/${id}`),
  });

  const grouped = useMemo(() => {
    if (!data) return [];
    const cats = data.menu_categories || [];
    const buckets = new Map();
    (data.products || []).forEach((p) => {
      const key = p.category_id || "_uncat";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(p);
    });
    const out = cats
      .filter((c) => buckets.has(c.category_id))
      .map((c) => ({ id: c.category_id, name: c.name, items: buckets.get(c.category_id) }));
    if (buckets.has("_uncat")) out.push({ id: "_uncat", name: "More", items: buckets.get("_uncat") });
    return out.length ? out : [{ id: "_all", name: "Menu", items: data.products || [] }];
  }, [data]);

  if (isLoading)
    return (
      <div className="space-y-4">
        <Skeleton className="h-56 w-full rounded-2xl" />
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  if (isError) return <ErrorState onRetry={refetch} />;

  const activeTab = tab || grouped[0]?.id;
  const qtyOf = (pid) => cart.items[pid]?.quantity || 0;

  return (
    <div className="pb-28">
      {/* Cover */}
      <div className="relative -mx-4 -mt-6 h-56 overflow-hidden lg:-mx-6 lg:rounded-b-3xl">
        {data.banner_url ? (
          <img src={fileUrl(data.banner_url)} alt={data.name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-secondary/30 to-primary/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1A18] via-[#1E1A18]/40 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 text-white lg:left-6">
          <h1 className="font-display text-3xl">{data.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" />
              {(data.rating ?? 0).toFixed(1)} ({data.review_count ?? 0})
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {data.eta_minutes ?? 30} min
            </span>
            <span>{formatRands(data.delivery_fee_base)} delivery</span>
          </div>
        </div>
      </div>

      {data.description && <p className="mt-4 text-muted-foreground">{data.description}</p>}

      {/* Category tabs */}
      {grouped.length > 1 && (
        <div className="sticky top-16 z-10 -mx-4 mt-4 flex gap-2 overflow-x-auto border-b bg-background/90 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          {grouped.map((g) => (
            <button
              key={g.id}
              onClick={() => setTab(g.id)}
              className={cn(
                "shrink-0 rounded-full px-3.5 py-1.5 text-sm transition-colors",
                activeTab === g.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
              )}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      {/* Products */}
      <div className="mt-6 space-y-3">
        {(grouped.find((g) => g.id === activeTab)?.items || []).length === 0 ? (
          <EmptyState title="No products listed right now." />
        ) : (
          (grouped.find((g) => g.id === activeTab)?.items || []).map((p) => {
            const qty = qtyOf(p.product_id);
            return (
              <div
                key={p.product_id}
                className="flex items-center gap-4 rounded-2xl border bg-card p-3"
                data-testid={`product-${p.product_id}`}
              >
                {p.image_url && (
                  <img src={fileUrl(p.image_url)} alt={p.name} className="h-20 w-20 shrink-0 rounded-xl object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-display text-base leading-tight">{p.name}</p>
                  {p.description && <p className="line-clamp-1 text-sm text-muted-foreground">{p.description}</p>}
                  <p className="mt-1 text-sm">
                    {p.sale_price ? (
                      <>
                        <span className="text-primary">{formatRands(p.sale_price)}</span>{" "}
                        <span className="text-muted-foreground line-through">{formatRands(p.price)}</span>
                      </>
                    ) : (
                      formatRands(p.price)
                    )}
                  </p>
                </div>
                {qty > 0 ? (
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => cart.setQty(p.product_id, qty - 1)} data-testid={`decr-${p.product_id}`}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-5 text-center text-sm">{qty}</span>
                    <Button size="icon" className="h-8 w-8 rounded-full" onClick={() => cart.add(data, p)} data-testid={`incr-${p.product_id}`}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" className="rounded-full" onClick={() => cart.add(data, p)} data-testid={`add-${p.product_id}`}>
                    <Plus className="mr-1 h-4 w-4" /> Add
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Sticky cart bar */}
      {cart.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 p-4 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-0 lg:px-6">
            <div className="text-sm">
              <span className="font-display text-base">{cart.count} item{cart.count > 1 ? "s" : ""}</span>
              <span className="ml-2 text-muted-foreground">{formatRands(cart.subtotal)}</span>
            </div>
            <Button className="ml-auto rounded-full" onClick={() => navigate("/cart")} data-testid="view-cart-button">
              View cart
            </Button>
          </div>
        </div>
      )}

      <div className="mt-8 text-sm">
        <Link to="/" className="text-muted-foreground hover:text-foreground">
          ← Back to stores
        </Link>
      </div>
    </div>
  );
}
